import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useId, useState } from "react";
import { api, errorMessage, type ProviderAuthStatus } from "../api";
import { untilText } from "../format";
import { Icon } from "../icons";
import { ConfirmDialog, useToast } from "./feedback";

type Mode = "subscription" | "apiKey";

const MODE_NAME: Record<Mode, string> = { subscription: "Abo-Modus", apiKey: "API-Reserve" };

function nodesText(count: number, setup: string): string {
  if (count === 0) return `Kein Node meldet „${setup}“.`;
  return `${count} ${count === 1 ? "Node meldet" : "Nodes melden"} „${setup}“.`;
}

function AuthSwitch({
  name,
  description,
  on,
  disabled,
  onToggle,
  children,
}: {
  name: string;
  description: string;
  on: boolean;
  disabled: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) {
  const labelId = useId();
  const descId = useId();
  return (
    <li className="auth-row">
      <div className="auth-text">
        <strong id={labelId}>{name}</strong>
        <p id={descId}>{description}</p>
        {children}
      </div>
      <button
        type="button"
        role="switch"
        className="switch"
        aria-checked={on}
        aria-labelledby={labelId}
        aria-describedby={descId}
        disabled={disabled}
        onClick={onToggle}
      >
        <span className="switch-track" aria-hidden="true">
          <span className="switch-knob" />
        </span>
        <span className="switch-text" aria-hidden="true">
          {on ? "an" : "aus"}
        </span>
      </button>
    </li>
  );
}

/**
 * Owner-Modus nach ADR-016: Abo des Owners und API-Reserve schalten. Nur der Owner darf ändern;
 * Abschalten beendet offene Läufe des Modus und verlangt deshalb eine Bestätigung.
 */
export function ProviderAuthPanel({ isOwner }: { isOwner: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const status = useQuery({ queryKey: ["provider-auth"], queryFn: api.providerAuth });
  const [confirm, setConfirm] = useState<Mode | null>(null);
  const save = useMutation({
    mutationFn: (input: { next: { subscription: boolean; apiKey: boolean }; mode: Mode }) =>
      api.setProviderAuth(input.next),
    onSuccess: async (result, { next, mode }) => {
      queryClient.setQueryData<ProviderAuthStatus>(["provider-auth"], result);
      setConfirm(null);
      toast(`${MODE_NAME[mode]} ${next[mode] ? "eingeschaltet" : "abgeschaltet"}`);
      await queryClient.invalidateQueries({ queryKey: ["work-orders"] });
    },
    onError: (err) => {
      setConfirm(null);
      toast(errorMessage(err), "error");
    },
  });
  const closeConfirm = useCallback(() => setConfirm(null), []);

  const s = status.data;
  const current = { subscription: s?.subscriptionEnabled ?? false, apiKey: s?.apiKeyEnabled ?? false };
  const apply = (mode: Mode) => save.mutate({ next: { ...current, [mode]: !current[mode] }, mode });
  const toggle = (mode: Mode) => (current[mode] ? setConfirm(mode) : apply(mode));
  const other: Mode | null =
    confirm === "subscription" ? "apiKey" : confirm === "apiKey" ? "subscription" : null;

  return (
    <section className="panel auth-panel" aria-labelledby="authTitle">
      <div className="panel-head">
        <h2 id="authTitle">Zugang zu Claude Code</h2>
        <span className="chip chip--ghost">Owner-Modus</span>
      </div>
      {status.isError ? (
        <p className="form-error" role="alert">
          <Icon name="alert" /> {errorMessage(status.error)}
        </p>
      ) : !s ? (
        <p className="muted">Lade Zugang …</p>
      ) : (
        <>
          {s.subscriptionPausedUntil && (
            <p className="auth-paused" role="status">
              <Icon name="clock" />
              <span>
                <strong>Abo pausiert bis {untilText(s.subscriptionPausedUntil)}.</strong> Das Abo-Limit ist
                erreicht. Betroffene Aufträge laufen danach selbst weiter, ohne Wechsel auf den API-Schlüssel.
              </span>
            </p>
          )}
          <ul className="auth-list">
            <AuthSwitch
              name="Abo-Modus"
              description="Läufe, die du beauftragst, nutzen dein Claude-Abo. Höchstens ein Abo-Lauf gleichzeitig, kein Dollarbetrag auf dem Budget. Aufträge anderer Menschen nutzen dein Abo nie."
              on={current.subscription}
              disabled={!isOwner || save.isPending}
              onToggle={() => toggle("subscription")}
            >
              {s.subscriptionEnabled && !s.subscriptionIsMine && (
                <p className="auth-hint">Eingeschaltet von einem anderen Menschen, nicht dein Abo.</p>
              )}
              <p
                className={`auth-hint${s.subscriptionEnabled && s.nodesWithSubscription === 0 ? " is-warn" : ""}`}
              >
                <Icon name="server" /> {nodesText(s.nodesWithSubscription, "Abo-Modus eingerichtet")}
                {s.subscriptionEnabled &&
                  s.nodesWithSubscription === 0 &&
                  " Abo-Läufe warten, bis einer das meldet."}
              </p>
            </AuthSwitch>
            <AuthSwitch
              name="API-Reserve"
              description="Läufe über den API-Schlüssel auf dem Node, mit gemessenen Kosten auf dem Budget. Gilt auch für Aufträge anderer Menschen."
              on={current.apiKey}
              disabled={!isOwner || save.isPending}
              onToggle={() => toggle("apiKey")}
            >
              <p className={`auth-hint${s.apiKeyEnabled && s.nodesWithApiKey === 0 ? " is-warn" : ""}`}>
                <Icon name="server" /> {nodesText(s.nodesWithApiKey, "API-Modus eingerichtet")}
              </p>
            </AuthSwitch>
          </ul>
          {!isOwner && <p className="muted">Nur der Owner kann Abo-Modus und API-Reserve schalten.</p>}
          <p className="muted small">
            Zugangsdaten liegen nur auf dem Node. szyOS kennt lediglich, welcher Modus eingerichtet ist.
          </p>
        </>
      )}
      {confirm && other && (
        <ConfirmDialog
          title={`${MODE_NAME[confirm]} abschalten?`}
          confirmLabel="Abschalten"
          busy={save.isPending}
          onConfirm={() => apply(confirm)}
          onCancel={closeConfirm}
        >
          <p>
            Laufende und wartende Läufe im {confirm === "subscription" ? "Abo-Modus" : "API-Modus"} enden
            sofort, wie beim Kill Switch.
          </p>
          <p>
            {current[other]
              ? `Ihre Aufgaben laufen ${other === "apiKey" ? "mit der API-Reserve" : "im Abo-Modus"} weiter.`
              : "Weil kein anderer Modus eingeschaltet ist, blockieren betroffene Aufträge sichtbar, bis du wieder einschaltest."}
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
